import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { CText, CLoader } from '../../components/common';
import { IoArrowBack, IoTrendingUp, IoTrendingDown } from 'react-icons/io5';
import { fetchBucketStocks, BucketDetail as BucketDetailType, BucketStock } from '../../api/buckets';

const getRatingColor = (rating: string | null, colors: any) => {
  switch (rating) {
    case 'strong_buy':
      return colors.green;
    case 'buy':
      return '#4CAF50';
    case 'hold':
      return colors.grayScale500;
    case 'sell':
      return '#FF9800';
    case 'strong_sell':
      return colors.alertColor;
    default:
      return colors.grayScale400;
  }
};

const getRatingLabel = (rating: string | null) => {
  switch (rating) {
    case 'strong_buy':
      return 'Strong Buy';
    case 'buy':
      return 'Buy';
    case 'hold':
      return 'Hold';
    case 'sell':
      return 'Sell';
    case 'strong_sell':
      return 'Strong Sell';
    default:
      return 'N/A';
  }
};

const BucketDetail: React.FC = () => {
  const { bucketName } = useParams<{ bucketName: string }>();
  const navigate = useNavigate();
  const colors = useAppSelector((state) => state.theme.theme);

  const [bucketData, setBucketData] = useState<BucketDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const decodedBucketName = bucketName ? decodeURIComponent(bucketName) : '';

  useEffect(() => {
    const loadBucketStocks = async () => {
      if (!decodedBucketName) return;

      try {
        setLoading(true);
        setError(null);
        const data = await fetchBucketStocks(decodedBucketName);
        setBucketData(data);
      } catch (err) {
        console.error('Failed to fetch bucket stocks:', err);
        setError('Failed to load bucket data');
      } finally {
        setLoading(false);
      }
    };
    loadBucketStocks();
  }, [decodedBucketName]);

  return (
    <div
      className="p-4 lg:p-8 min-h-screen pb-24"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      <div className="lg:max-w-6xl lg:mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:opacity-80 transition-opacity"
            style={{ backgroundColor: colors.inputBackground }}
          >
            {React.createElement(IoArrowBack as any, { size: 20, color: colors.textColor })}
          </button>
          <div>
            <CText type="B20" color={colors.textColor}>
              {decodedBucketName}
            </CText>
            {bucketData && (
              <CText type="r14" color={colors.grayScale500}>
                {bucketData.stocks.length} stocks in this bucket
              </CText>
            )}
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <CLoader />
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="text-center py-12">
            <CText type="M16" color={colors.alertColor}>
              {error}
            </CText>
            <button
              className="mt-4 px-4 py-2 rounded-lg"
              style={{ backgroundColor: colors.primary, color: colors.white }}
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && bucketData?.stocks.length === 0 && (
          <div className="text-center py-12">
            <CText type="M16" color={colors.grayScale500}>
              No stocks in this bucket yet
            </CText>
            <CText type="r14" color={colors.grayScale400} style={{ marginTop: 8 }}>
              Stocks will be added by the discovery agent
            </CText>
          </div>
        )}

        {/* Stocks Grid */}
        {!loading && !error && bucketData && bucketData.stocks.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {bucketData.stocks.map((stock, index) => (
              <StockCard
                key={`${stock.stock_name}-${index}`}
                stock={stock}
                colors={colors}
                onClick={() => navigate(`/company/${encodeURIComponent(stock.stock_name)}`)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

interface StockCardProps {
  stock: BucketStock;
  colors: any;
  onClick: () => void;
}

const StockCard: React.FC<StockCardProps> = ({ stock, colors, onClick }) => {
  const financialInfo = stock.financial_info || {};
  const hasFinancialData = Object.keys(financialInfo).length > 0;

  return (
    <div
      className="p-4 rounded-xl border hover:shadow-lg transition-all cursor-pointer hover:scale-[1.02]"
      style={{
        backgroundColor: colors.inputBackground,
        borderColor: colors.borderColor,
      }}
      onClick={onClick}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <CText type="M14" color={colors.textColor}>
            {stock.stock_name}
          </CText>
          {stock.stock_symbol && (
            <CText type="r12" color={colors.grayScale500}>
              {stock.stock_symbol}
            </CText>
          )}
        </div>
        {stock.rating && (
          <span
            className="text-xs px-2 py-1 rounded-full font-medium"
            style={{
              backgroundColor: getRatingColor(stock.rating, colors) + '20',
              color: getRatingColor(stock.rating, colors),
            }}
          >
            {getRatingLabel(stock.rating)}
          </span>
        )}
      </div>

      {/* Analysis */}
      {stock.analysis && (
        <CText
          type="r12"
          color={colors.grayScale500}
          style={{
            marginBottom: 12,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {stock.analysis}
        </CText>
      )}

      {/* Financial Info */}
      {hasFinancialData && (
        <div
          className="pt-3 grid grid-cols-2 gap-2"
          style={{ borderTop: `1px solid ${colors.borderColor}` }}
        >
          {financialInfo.market_cap && (
            <div>
              <CText type="r10" color={colors.grayScale400}>
                Market Cap
              </CText>
              <CText type="M12" color={colors.textColor}>
                {formatMarketCap(financialInfo.market_cap)}
              </CText>
            </div>
          )}
          {financialInfo.pe_ratio && (
            <div>
              <CText type="r10" color={colors.grayScale400}>
                P/E Ratio
              </CText>
              <CText type="M12" color={colors.textColor}>
                {financialInfo.pe_ratio.toFixed(1)}
              </CText>
            </div>
          )}
          {financialInfo.revenue_growth !== undefined && (
            <div className="flex items-center gap-1">
              <div>
                <CText type="r10" color={colors.grayScale400}>
                  Revenue Growth
                </CText>
                <div className="flex items-center gap-1">
                  {React.createElement(
                    (financialInfo.revenue_growth >= 0 ? IoTrendingUp : IoTrendingDown) as any,
                    {
                      size: 12,
                      color: financialInfo.revenue_growth >= 0 ? colors.green : colors.alertColor,
                    }
                  )}
                  <CText
                    type="M12"
                    color={financialInfo.revenue_growth >= 0 ? colors.green : colors.alertColor}
                  >
                    {financialInfo.revenue_growth.toFixed(1)}%
                  </CText>
                </div>
              </div>
            </div>
          )}
          {financialInfo.profit_margin !== undefined && (
            <div>
              <CText type="r10" color={colors.grayScale400}>
                Profit Margin
              </CText>
              <CText type="M12" color={colors.textColor}>
                {financialInfo.profit_margin.toFixed(1)}%
              </CText>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const formatMarketCap = (value: number): string => {
  if (value >= 100000) {
    return `${(value / 100000).toFixed(1)}L Cr`;
  } else if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K Cr`;
  }
  return `${value} Cr`;
};

export default BucketDetail;
